import { useMemo, useCallback } from 'react';
import { useFloating, offset, shift, flip, useTransitionStyles, autoUpdate } from '@floating-ui/react';
import { Marker } from 'react-map-gl';
import { MarkerEvent } from 'react-map-gl/dist/esm/types';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import 'twin.macro';

import { JobLocationCoordinates, useRestaurantsService } from '@restworld/data-services';

import { Pin } from '../pin';
import { useJobPositionApiData } from '../../../../hooks';
import PopupInfoEl, { isRestaurantMarkerAtom } from '../popup-info-el';
import {
  explorerRestaurantSelectedMarkerAtom,
  explorerRestaurantListAtom,
} from '../../job-explorer-restaurant-explorer.atoms';
import {
  setPopupLocationCoordinatesAtom,
  setPopupDetailsAtom,
  mapPopupLoadingAtom,
} from '../../job-explorer-map.atoms';
import { activeJpIdAtom, jobLocationPositionListAtom } from '../../job-explorer-list.atoms';
import { JobLocationCoordinatesClusterType } from '../job-explorer-map.hooks/use-cluster';

enum PaymentTypeAbbr {
  hour = 'o',
  service = 'g',
  month = 'm',
}

type MarkerWithFloatingPopupProps = {
  isOpen: boolean;
  setIsOpen?: (open: boolean) => void;
  cluster?: JobLocationCoordinatesClusterType;
  isRestaurantMarker?: boolean;
};

const ARROW_WIDTH = 30;
const ARROW_HEIGHT = 15;

const MarkerWithFloatingPopup = ({ isOpen, setIsOpen, cluster, isRestaurantMarker }: MarkerWithFloatingPopupProps) => {
  const setPopupDetails = useSetAtom(setPopupDetailsAtom);
  const setPopupLocationCoordinates = useSetAtom(setPopupLocationCoordinatesAtom);
  const jobLocationPositionList = useAtomValue(jobLocationPositionListAtom);
  const setMapPopupLoading = useSetAtom(mapPopupLoadingAtom);
  const [activeJpId, setActiveJpId] = useAtom(activeJpIdAtom);
  const { getJobPositionApiCall } = useJobPositionApiData();
  const employerService = useRestaurantsService();
  const explorerRestaurantList = useAtomValue(explorerRestaurantListAtom);
  const setExplorerRestaurantSelectedMarker = useSetAtom(explorerRestaurantSelectedMarkerAtom);
  const setIsRestaurantMarker = useSetAtom(isRestaurantMarkerAtom);

  const {
    id: clusterId,
    label: clusterLabel,
    salary,
    min_salary: minSalary,
    max_salary: maxSalary,
    payment_type,
    coordinates,
  } = cluster?.extra || {};

  const paymentType = useMemo(() => `€${payment_type ? `/${PaymentTypeAbbr[payment_type]}` : ''}`, [payment_type]);

  const pinSubLabel = useMemo(() => {
    if (salary) return `${salary}${paymentType}`;
    if (minSalary) return `${minSalary}${maxSalary ? `-${maxSalary}` : '-n/a'}${paymentType}`;
    if (paymentType) return `- ${paymentType}`;
    return '-/-';
  }, [salary, minSalary, maxSalary, paymentType]);

  const getStyle = useCallback(
    (id: string, isRestaurant?: boolean) => ({
      zIndex: activeJpId === id ? 7 : isRestaurant ? 3 : 4,
    }),
    [activeJpId]
  );

  const handleMarker = useCallback(
    (e: MarkerEvent<mapboxgl.Marker, MouseEvent>, id: string, location: JobLocationCoordinates) => {
      e.originalEvent.stopPropagation();
      setActiveJpId(id);
      setPopupLocationCoordinates(location);
    },
    [setActiveJpId, setPopupLocationCoordinates]
  );

  const fetchRestaurantDetails = useCallback(
    async (selectedId: string) => {
      setExplorerRestaurantSelectedMarker({});
      const selected = explorerRestaurantList?.find((v) => v.id === selectedId);
      if (selected) {
        setExplorerRestaurantSelectedMarker(selected);
        return;
      }
      try {
        setMapPopupLoading(true);
        const data = (await employerService.showPublicRestaurant(selectedId)).data?.restaurant;
        setExplorerRestaurantSelectedMarker(data);
      } catch (error) {
        console.error(error, 'error getting company profile');
      } finally {
        setMapPopupLoading(false);
      }
    },
    [employerService, explorerRestaurantList, setExplorerRestaurantSelectedMarker, setMapPopupLoading]
  );

  const fetchJobDetails = useCallback(
    async (id: string) => {
      const selectedPosition = jobLocationPositionList?.find((v) => v.id === id);
      if (selectedPosition) {
        setPopupDetails(selectedPosition);
        return;
      }
      try {
        setMapPopupLoading(true);
        const data = await getJobPositionApiCall(id);
        setPopupDetails(data);
      } catch (error) {
        console.error(error, `error fetching showJobPosition`);
      } finally {
        setMapPopupLoading(false);
      }
    },
    [getJobPositionApiCall, jobLocationPositionList, setMapPopupLoading, setPopupDetails]
  );

  const fetchMarkerDetails = useCallback(
    async (id: string) => {
      if (isRestaurantMarker) {
        setIsRestaurantMarker(true);
        await fetchRestaurantDetails(id);
      } else {
        await fetchJobDetails(id);
      }
    },
    [isRestaurantMarker, setIsRestaurantMarker, fetchRestaurantDetails, fetchJobDetails]
  );

  const handleClick = useCallback(
    (e: MarkerEvent<mapboxgl.Marker, MouseEvent>, id: string, location: JobLocationCoordinates) => {
      e.originalEvent.stopPropagation();
      handleMarker(e, id, location);
      setPopupDetails({});
      setIsRestaurantMarker(false);
      fetchMarkerDetails(id);
    },
    [handleMarker, setPopupDetails, setIsRestaurantMarker, fetchMarkerDetails]
  );

  const { refs, floatingStyles, context, middlewareData } = useFloating({
    placement: 'right',
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(ARROW_HEIGHT), flip({ padding: 5 }), shift({ padding: 5 })],
    whileElementsMounted: autoUpdate,
  });

  const arrowX = middlewareData.arrow?.x ?? 0;
  const arrowY = middlewareData.arrow?.y ?? 0;
  const transformX = arrowX + ARROW_WIDTH / 2;
  const transformY = arrowY + ARROW_HEIGHT;

  const { styles } = useTransitionStyles(context, {
    initial: { transform: 'scale(0)' },
    common: ({ side }) => ({
      transformOrigin: {
        top: `${transformX}px calc(100% + ${ARROW_HEIGHT}px)`,
        bottom: `${transformX}px ${-ARROW_HEIGHT}px`,
        left: `calc(100% + ${ARROW_HEIGHT}px) ${transformY}px`,
        right: `${-ARROW_HEIGHT}px ${transformY}px`,
      }[side],
    }),
  });

  if (!cluster) return null;

  return (
    <Marker
      key={`marker-${clusterId}`}
      longitude={cluster.geometry.coordinates[0]}
      latitude={cluster.geometry.coordinates[1]}
      onClick={(e) => handleClick(e, clusterId, cluster.extra)}
      style={getStyle(clusterId, isRestaurantMarker)}
    >
      <div ref={refs.setReference} className="reference">
        <Pin
          label={clusterLabel}
          isActive={activeJpId === clusterId}
          subLabel={pinSubLabel}
          isApproximate={coordinates?.is_approximate}
          isRestaurantMarker={isRestaurantMarker}
        />
      </div>
      {isOpen && (
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          tw="hidden md:block"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div style={styles} className="floating">
            <PopupInfoEl />
          </div>
        </div>
      )}
    </Marker>
  );
};

export default MarkerWithFloatingPopup;
